import React, { createContext, useContext, useState, ReactNode } from 'react';
import { trackHeroViewSwitched } from '../analytics';
import type { HeroView } from '../analytics';

interface HeroViewContextProps {
	view: HeroView;
	switchView: (next: HeroView, method: 'click' | 'keyboard') => void;
}

const HeroViewContext = createContext<HeroViewContextProps | undefined>(undefined);

const getInitialView = (): HeroView =>
	typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('view') === 'mycoach'
		? 'my_coach'
		: 'assessment';

/** Shared homepage view (Assessment Service / MyCoach), so the hero, nav CTA and sections all swap together. */
export const HeroViewProvider = ({ children }: { children: ReactNode }) => {
	const [view, setView] = useState<HeroView>(getInitialView);

	const switchView = (next: HeroView, method: 'click' | 'keyboard') => {
		if (next === view) return;
		trackHeroViewSwitched(view, next, method);
		setView(next);

		const url = new URL(window.location.href);
		if (next === 'my_coach') url.searchParams.set('view', 'mycoach');
		else url.searchParams.delete('view');
		window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
	};

	return <HeroViewContext.Provider value={{ view, switchView }}>{children}</HeroViewContext.Provider>;
};

export const useHeroView = () => {
	const context = useContext(HeroViewContext);
	if (!context) {
		throw new Error('useHeroView must be used within a HeroViewProvider');
	}
	return context;
};

/** For shared components (e.g. the nav) that also render on pages without a hero toggle. */
export const useOptionalHeroView = (): HeroViewContextProps | undefined => useContext(HeroViewContext);
